import { ComponentChildren, createContext } from 'preact';
import { AppRouter, PageDef, RouteParams, RouteLoadProps } from './async-router';
import { Auth, emptyContext, useAuth } from './session-context';
import { Dispatch, StateUpdater, useState, useContext, useRef, useEffect } from 'preact/hooks';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { toQueryString } from 'shared/urls';
import { hasLevel } from 'shared/auth';
import { showError } from '@components/app-error';
import { LoadingIndicator } from '@components/loading-indicator';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';
import { setSentryUser } from 'client/lib/sentry';
import { nextPageKey } from './page-key';

type LoadingRoute = {
  url: string;
  params: RouteParams;
  loadModule(): Promise<{ route: PageDef }>;
};

type CurrentRoute = LoadingRoute & {
  data: any;
  router: AppRouter;
  auth: Auth;
  def: PageDef;
};

type RouteState = {
  current?: CurrentRoute;
  loading?: LoadingRoute;
};

type RouteContext = {
  data: any;
  router: AppRouter;
  def: PageDef;
  url: string;
  auth: Auth;
  params: RouteParams;
};

export function errRedirect(url: string, hard?: boolean) {
  const err: any = new Error(`Redirect ${url}`);
  err.data = { redirectTo: url, hard };
  return err;
}

/**
 * The context which drives all of our useRoute* hooks.
 */
export const RouteContext = createContext<RouteContext>({
  router: {} as any,
  auth: emptyContext,
  url: '',
  params: {},
  data: undefined,
  def: {
    Page() {
      return null;
    },
  },
});

/*
 * Returns all the route params.
 */
export function useRouteParams() {
  return useContext(RouteContext).params;
}

/**
 * Return the active router.
 */
export function useRouter() {
  return useContext(RouteContext).router;
}

/*
 * Returns the route data.
 */
export function useRouteData<T>() {
  return useContext(RouteContext).data as T;
}

/*
 * Returns the route definition.
 */
export function useRouteDef() {
  return useContext(RouteContext).def;
}

/*
 * Returns the route context.
 */
export function useRouteContext() {
  return useContext(RouteContext);
}

function useRouteLoader(
  router: AppRouter,
  auth: Auth,
  state: RouteState,
  setState: Dispatch<StateUpdater<RouteState>>,
) {
  // This is a hacky way to check if we've reloaded the same
  // route several times without completing the load / resolve.
  // If loadCount exceeds 1, we're possibly in an infinite loop
  // e.g. the page load function is *always* rewriting the route
  // to the same place, possibly with slightly tweaked params, but
  // this is an error state, so we'll bail early.
  const loadCount = useRef({ url: '', count: 0 });

  if (loadCount.current.url === state.loading?.url) {
    ++loadCount.current.count;
  } else {
    loadCount.current.url = state.loading?.url || '';
    loadCount.current.count = 0;
  }

  useAsyncEffect(async () => {
    let hasRouteChanged = false;
    if (loadCount.current.count > 1) {
      console.error('Possible ininite load loop detected:', loadCount.current);
      return;
    }
    if (!state.loading) {
      return;
    }

    const { url, params, loadModule } = state.loading;
    const module = await loadModule();
    const def = module.route;
    if (!def) {
      throw new Error(`Could not find page "${url}"`);
    }
    if (hasRouteChanged) {
      return;
    }
    def.processParams?.(params);
    await tryLoad({
      url,
      params,
      auth,
      def,
      router,
      async load() {
        if (!def.load) {
          setState({
            current: { url, data: {}, params, router, auth, def, loadModule },
          });
          return;
        }
        const data = await def.load({ url, params, auth, router });
        if (hasRouteChanged) {
          return;
        }
        setState((s) => {
          // If the route changed while we were loading, we won't modify
          // the state, since it belongs to a different route.
          if (s.current !== state.current || s.loading !== state.loading) {
            return s;
          }
          return { current: { url, data, params, router, auth, def, loadModule } };
        });
      },
    });
    return () => {
      hasRouteChanged = true;
    };
  }, [state.loading?.url, state.loading?.params]);
}

/**
 * Handle standard loading errors.
 */
async function tryLoad({
  def,
  url,
  params,
  router,
  auth,
  load,
}: Pick<RouteLoadProps, 'url' | 'params' | 'router' | 'auth'> & {
  def: PageDef<any>;
  load(): Promise<unknown>;
}) {
  try {
    const hasCheckoutPass = !!def.allowCheckoutPass && !!auth.config?.checkoutPass;
    if (!def.isPublic && !auth.user && !hasCheckoutPass) {
      // If login is required, redirect
      router.goto(
        `${def.loginPage || '/login'}?${toQueryString({
          ...params,
          redirect: encodeURIComponent(location.href.slice(location.origin.length)),
        })}`,
      );
    } else if (def.authLevel && !hasLevel(auth.user, def.authLevel)) {
      // Unauthorized to view this page.
      router.goto('/');
    } else {
      // Good to go... perform the load
      return await load();
    }
  } catch (err) {
    if (err.data?.domain) {
      // Certain Boom errors contain a domain; do a hard redirect.
      const url = new URL(location.href);
      url.hostname = err.data.domain;
      location.assign(url);
    } else if (err.data?.redirectTo) {
      // If Boom data contains a redirect URL respect it.
      if (err.data.hard) {
        location.assign(err.data.redirectTo);
      } else {
        router.goto(err.data.redirectTo);
      }
    } else if (
      url !== '' &&
      url !== '/' &&
      (['Forbidden', 'Unauthorized'].includes(err?.error) || [401, 403].includes(err?.statusCode))
    ) {
      router.goto('/');
    } else if (err?.href) {
      router.goto(err.href);
    } else {
      showError(err);
    }
  }
}

/**
 * If the route params change, and we have a laodSubroute defined,
 * this hook loads the sub route and updates the state accordingly.
 */
function useSubrouteLoader(props: RouteContext, setState: Dispatch<StateUpdater<any>>) {
  const [isLoading, setIsLoading] = useState(false);
  const latestLoad = useRef(0);

  useDidUpdateEffect(async () => {
    const loadSubroute = props.def.loadSubroute;
    // Latest wins. The effect is async, so it cannot return a cleanup; count
    // loads instead, and let only the newest one write state. Otherwise a
    // slower earlier load (quick clicks A → B → C) lands last and shows B.
    const thisLoad = ++latestLoad.current;
    const isLatest = () => thisLoad === latestLoad.current;
    const setLatestState: typeof setState = (update) => {
      if (isLatest()) {
        setState(update);
      }
    };

    function scrollToTop() {
      if (!props.def.disableSubrouteScroll) {
        document.body.scrollIntoView();
      }
    }

    if (!loadSubroute) {
      scrollToTop();
      return;
    }
    try {
      setIsLoading(true);
      await tryLoad({ ...props, load: () => loadSubroute(props, setLatestState) });
    } finally {
      if (isLatest()) {
        scrollToTop();
        setIsLoading(false);
      }
    }
  }, [props.params]);

  return isLoading;
}

function Page(props: RouteContext) {
  const Child = props.def.Page;
  const [state, setState] = useState<any>(props.data);
  const isLoading = useSubrouteLoader(props, setState);

  return (
    <>
      {isLoading && <LoadingIndicator />}
      <Child {...props} state={state} setState={setState} router={props.router} />
    </>
  );
}

/**
 * Derive the router page state from the previous state and the new url and params.
 */
function makeState(
  route: [url: string, params: RouteParams],
  router: AppRouter,
  state: RouteState,
) {
  const [url, params] = route;
  const { loadModule } = router.definition(url);
  let currentKey: string | undefined = '';
  let nextKey: string | undefined = '';

  // If we're on the same page / module, but the keys change, we
  // want to perform a load.
  if (state.current && state.current.loadModule === loadModule) {
    currentKey = state.current.def.key?.(state.current);
    nextKey = nextPageKey(state.current.def, state.current, params);
  }

  // We compare the loadModule function rather than the URL pattern,
  // since a single page may have multiple URLs map to it, but should
  // only have one loadModule function defined, so this prevents us
  // from re-loading pages unnecessarily, provided the page keys remain
  // the same.
  if (state.current?.loadModule === loadModule && currentKey === nextKey) {
    state.current?.def.processParams?.(params);
    return { ...state, current: { ...state.current, url, params } };
  }
  return { ...state, loading: { url, params, loadModule } };
}

/**
 * Handle routing and render the app based on the current / loading route.
 */
export function RouterPage({
  router,
  children,
}: {
  router: AppRouter;
  children: ComponentChildren;
}) {
  const auth = useAuth();
  const [state, setState] = useState<RouteState>(() => {
    const result = makeState(router.init(), router, {});
    router.onLocationChange((url, params) => setState((s) => makeState([url, params], router, s)));
    return result;
  });

  useRouteLoader(router, auth, state, setState);

  useEffect(() => {
    if (!state.loading) {
      document.body.scrollIntoView();
    }
  }, [state.loading]);

  // Update Sentry user when the user changes
  useEffect(() => {
    setSentryUser(auth.user);
  }, [auth.user]);

  if (!state.current) {
    return (
      <>
        <LoadingIndicator />
        {children}
      </>
    );
  }

  return (
    <RouteContext.Provider value={state.current}>
      {state.loading && <LoadingIndicator />}
      {children}
      <Page
        key={state.current.def.key?.(state.current) || state.current.loadModule}
        {...state.current}
      />
    </RouteContext.Provider>
  );
}
